import React, { useState, useEffect } from 'react'
import Header from '../Header/Header';
import { useNavigate } from 'react-router-dom';
import { CustomTable, ConfirmationPopup, SuccessModal, Search, Pagination, Loader } from '@ohif/ui';
import './index.css'
import { _userUrls } from '@custom/Utils/constants/apiUrls';
import { customStyle, getCurrencyContact } from '@custom/Utils/utils';
import { useSelector, useDispatch } from 'react-redux';
import { AppDispatch, RootState } from '@custom/store/store';
import userList from '@custom/rtk-apis/user/userList';
import { closeUserListModal } from '@custom/store/slices/user/getUserList';
import deleteUser from '@custom/rtk-apis/user/deleteUser';
import { closeUserDeleteModal } from '@custom/store/slices/user/deleteUser';
import deactivateUserApi from '@custom/rtk-apis/user/deactivateUser';
import activateUserApi from '@custom/rtk-apis/user/activateUser';
import { closeDeactivateUserListModal } from '@custom/store/slices/user/deactivateUser';
import { useTheme } from "../../ui/src/contextProviders"
import hospitalDetailApi from '@custom/rtk-apis/hospital/hospitalDetails';
import getBillingGeneralSettings from '@custom/rtk-apis/billing/getGeneralSettings';
import { servicesManager } from '../../app/src/App';

const Users = () => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const [isSuccessModalOpen, setIsSuccessModal] = useState(false);
  const [selectedRow, setSelectedRow] = useState(null);
  const [title, setTitle] = useState('');
  const [tableData, setTableData] = useState([])
  const [pgNumber, setPgNumber] = useState(1);
  const [pgLimit, setPgLimit] = useState(30);
  const [search, setSearch] = useState('');
  const [totalPg, setTotalPg] = useState(1000000);
  const role = sessionStorage.getItem('role') || localStorage.getItem('role');
  const [buttonText, setButtonText] = useState("Deactivated Users");
  const [showDeleteAction, setShowDeleteAction] = useState(true);
  const [actionType, setActionType] = useState('');
  const { isDarkMode } = useTheme();
  const [searchInputValue, setSearchInputValue] = useState('');
  const [speechToTextAuthorized, setSpeechToTextAuthorized] = useState(false);
  const { uiNotificationService } = servicesManager.services;
  const [isSearching, setIsSearching] = useState(false);

  const { msg, result, loading, status } = useSelector((state: RootState) => state?.userListResponse);
  const deleteUserResponse = useSelector((state: RootState) => state?.deleteUserResponse);
  const { message: deactivateMsg, result: deactivateResult, loading: deactivateLoading, status: deactivateStatus } = useSelector((state: RootState) => state?.deactivateUserSliceResponse);
  const billingGeneralSettingsData = useSelector((state: RootState) => state.getBillingGeneralSettingsSliceResponse);

  const handleDeactivateUser = () => {
    if (buttonText === "Deactivated Users") {
      setButtonText("Activated Users");
      setShowDeleteAction(false);
    } else {
      setButtonText("Deactivated Users");
      setShowDeleteAction(true);
    }
    setPgNumber(1);
  };

  const columns = [
    { key: 'name', title: 'Name', sort: true },
    { key: 'email', title: 'Email ID', sort: true },
    { key: 'role', title: 'Role', sort: true },
    { key: 'hprId', title: 'HPR ID' },
    { key: 'contactDetails', title: 'Contact Details' },
    { key: 'licenseNumber', title: 'License Number' },
    { key: 'actions', title: 'Action' }
  ];

  const defaultCountryCode = billingGeneralSettingsData?.status && billingGeneralSettingsData?.result?.currency ? getCurrencyContact(billingGeneralSettingsData.result.currency) : '+91';

  const filteredColumns = role === 'Front Desk'
    ? columns.filter(column => column.key !== 'actions')
    : columns;

  const roles = result?.list?.map(item => item.role);

  const actions = [
    {
      label: 'Edit',
      iconName: 'edit',
      onClick: (item) => {
        if (item?.userDetail?.status === 'delete') {
          setSelectedRow(item);
          setActionType('activate');
          setIsPopupOpen(true);
        } else {
          navigate("/addUser", { state: { userData: item?.userDetail, speechToTextAuthorized: speechToTextAuthorized } });
        }
      }
    },
    ...(roles !== 'hospitalsuperadmin' && buttonText === "Deactivated Users" ? [{
      label: 'Deactivate',
      iconName: 'delete',
      onClick: (item) => {
        setSelectedRow(item);
        setActionType('deactivate');
        setIsPopupOpen(true);
      }
    }] : []),
  ];

  const handleActivateConfirm = (item) => {
    const userId = selectedRow?.userDetail?._id;

    if (!userId) {
      console.error('User ID is undefined');
      return;
    }

    dispatch(activateUserApi({ userId }))
      .then((res) => {
        const { status, msg } = res?.payload || {};
        if (status === "ERROR") {
          uiNotificationService.show({
            title: 'Error activating user',
            message: msg || 'An error occurred while activating the user.',
            type: 'error',
          });
          return;
        }
        setIsSuccessModal(true);
        setTitle('User activated successfully.');
        setTimeout(() => {
          setIsPopupOpen(false);
          setButtonText('Deactivated Users');
          setShowDeleteAction(true);
        }, 300);
      })
      .catch((error) => {
        console.error('Error activating user:', error);

        uiNotificationService.show({
          title: 'Error activating user',
          message: error?.message || 'Something went wrong. Please try again.',
          type: 'error',
        });
      });
  };

  const handleConfirm = async () => {
    if (actionType === 'deactivate') {
      await dispatch(deleteUser({ _id: selectedRow?.userDetail?._id }));
    } else if (actionType === 'activate') {
      await handleActivateConfirm(selectedRow?.userDetail?._id);
    }
    setIsPopupOpen(false);
  };

  const fetchhospitalDetail = async () => {
    const hospitalId = sessionStorage.getItem('hospitalId') || localStorage.getItem('hospitalId');
    await dispatch(hospitalDetailApi({ hospitalId })).unwrap()
      .then((res) => {
        setSpeechToTextAuthorized(res?.result.speechToText);
      });
  }

  const handleCancel = () => {
    setIsPopupOpen(false);
  };
  const navigateToPage = () => {
    navigate("/addUser")
  }
  const getHospitalId = () =>
    sessionStorage.getItem('hospitalId') || localStorage.getItem('hospitalId');

  const buildUserListParams = () => ({
    pgLimit,
    pageNumber: pgLimit * (pgNumber - 1),
    search,
    hospitalId: getHospitalId()
  });

  const fetchUserList = () => {
    dispatch(userList(buildUserListParams()));
  };

  const fetchDeactivatedUserList = () => {
    dispatch(deactivateUserApi(buildUserListParams()));
  };

  const searchClick = (searchText) => {
    setSearch(searchText);
    setSearchInputValue(searchText);
    setPgNumber(1);
    setIsSearching(true);
  };

  useEffect(() => {
    if (buttonText === 'Deactivated Users') {
      fetchUserList();
    } else {
      fetchDeactivatedUserList();
    }
  }, [buttonText, pgNumber, pgLimit, search]);

  useEffect(() => {
    fetchhospitalDetail();
  }, []);

  useEffect(() => {
    if (deleteUserResponse?.status === 'SUCCESS') {
      setIsSuccessModal(true);
      setTitle('User deleted successfully.');
      setTimeout(() => {
        setIsSuccessModal(false);
        fetchUserList();
      }, 800);
    }
    dispatch(closeUserDeleteModal());
  }, [deleteUserResponse]);

  useEffect(() => {
    const formatName = (name) =>
      name
        ? name
          .split(' ')
          .map(word => word.charAt(0).toUpperCase() + word.slice(1))
          .join(' ')
        : '--';

    const formatUserList = (list, countryCode) =>
      list?.map(item => ({
        name: formatName(item?.name),
        role: item?.role || '--',
        hprId: item?.hprId || '--',
        email: item?.email || '--',
        contactDetails: `${item?.contactNumberCountryCode || item?.countryCode || ''} ${item?.contactNumber || ''}`,
        licenseNumber: item?.licenseNumber || '--',
        userDetail: item
      })) || [];

    if (status === 'SUCCESS') {
      setTotalPg(result?.count);
      setTableData(formatUserList(result?.list, defaultCountryCode));
    } else if (deactivateStatus === 'SUCCESS') {
      setTotalPg(deactivateResult?.count);
      setTableData(formatUserList(deactivateResult?.list, deactivateResult?.list?.[0]?.contactNumberCountryCode));
    }

    dispatch(closeUserListModal());
    dispatch(closeDeactivateUserListModal());
  }, [status, deactivateStatus]);

  useEffect(() => {
    dispatch(getBillingGeneralSettings());
  }, []);

  const onPageChangeHandler = pg => {
    setPgNumber(pg);
  };

  const handlePageSizeChange = e => {
    setPgLimit(e);
  };

  const changeRoute = (item) => {
    navigate("/user/details/" + item?.userDetail?._id, {
      state: { userData: item }
    })
  };

  return (
    <>
      <div className='page page__fullBody' style={
        isDarkMode ? { background: '#090c29' } : customStyle.mainBackground
      }
      >
        <Header isBack={false} title='Users'></Header>
        <div className='page__body'>
          <div className='tableWrap'>
            <div className='tableWrap__head'>
              <div className="formRow formRow__2 m_0 w_100 formRow--flexEnd">
                <div className='formCol'>
                  <Search text="Name, Contact Details, Email ID or HPR ID" onSearchClick={searchClick} value={searchInputValue} setValue={setSearchInputValue}></Search>
                </div>
              </div>
              <div className='btn__group'>
                {role !== 'Front Desk' ? (
                  <>
                    <button className="btn btn__white" onClick={handleDeactivateUser}>
                      {buttonText}
                    </button>
                    <button className="btn btn__white" onClick={navigateToPage}>
                      <svg width="15" height="14" viewBox="0 0 15 14" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M7.63509 1.16699V12.8337M1.80176 7.00033H13.4684" stroke="#150823" strokeWidth="1.67" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                      Add User
                    </button>
                  </>
                ) : <></>}
              </div>
            </div>
            <div className='tableContainer'>
              <CustomTable columns={filteredColumns} data={tableData} actions={actions} searchQuery={search} userDetails={changeRoute} from="user" setClickedItem={''} clickedItem={''} />
            </div>
          </div>
        </div>
        {tableData.length > 0 && <Pagination
          onChangePage={onPageChangeHandler}
          onChangePerPage={handlePageSizeChange}
          totalPage={totalPg}
          currentPage={pgNumber}
          perPage={pgLimit}
        />}
      </div>
      <ConfirmationPopup
        isOpen={isPopupOpen}
        message={`Are you sure you want to ${actionType} ${selectedRow?.name}?`}
        onConfirm={handleConfirm}
        onCancel={handleCancel}
        title={''}
      />
      <SuccessModal
        isSuccessModalOpen={isSuccessModalOpen}
        title={title}
        handleCloseModal={() => setIsSuccessModal(false)}
      />
      <Loader isLoaderOpen={loading || deactivateLoading} />
    </>
  )
}

export default Users
